import { c as _c } from "react/compiler-runtime";
import Section from "@components/Section";
import { InterestRateChart, Period, PeriodSelect, filterDataByPeriod, type InterestRateChartType } from "@justhome/charts";
import { formatMoney } from "@justhome/common";
import { InterestRate } from "@justhome/api-core";
import { NumberField, Tabs, TabsContent, TabsList, TabsTrigger } from "@justhome/react-ui";
import { ComponentSectionsInterestRateChart } from "@lib/generated/graphql";
import clsx from "clsx";
import { FC, useMemo, useState } from "react";
import RateTable from "./RateTable";
export interface InterestRateChartComponentProps {
  data: ComponentSectionsInterestRateChart;
  staticData: {
    "60m": InterestRate[];
    "180m": InterestRate[];
  };
}
const chartOptions: {
  id: InterestRateChartType;
  label: string;
  description: (value?: number) => string;
  inputLabel?: string;
  defaultValue?: number;
}[] = [{
  id: "interest",
  label: "Zinsentwicklung",
  description: () => `Entwicklung des Sollzinssatzes für einen Immobilienkauf mit 90% Finanzierung und einer Darlehenssumme von ${formatMoney(300_000)}.`
}, {
  id: "rate",
  label: "Ratenentwicklung",
  description: value => `Entwicklung der monatlichen Rate für einen Immobilienkauf mit 90% Finanzierung und einer Darlehenssumme von ${formatMoney(value)}.`,
  inputLabel: "Kaufpreis",
  defaultValue: 300_000
}, {
  id: "budget",
  label: "Budgetentwicklung",
  description: value => `Entwicklung des Kaufbudgets für einen Immobilienkauf mit 90% Finanzierung bei einer leistbaren monatlichen Rate von ${formatMoney(value)}.`,
  inputLabel: "Monatliche Rate",
  defaultValue: 1_500
}];
const withFormula = (val: number, purchasePriceOrMonthlyRate: number, type: InterestRateChartType) => {
  if (type === "rate") {
    return (2 + val) / 100 * purchasePriceOrMonthlyRate * 0.9 / 12;
  }
  if (type === "budget") {
    return purchasePriceOrMonthlyRate * 12 / ((2 + val) / 100) / 0.9;
  }
  return val;
};
const InterestRateChartComponent: FC<InterestRateChartComponentProps> = t0 => {
  const $ = _c(32);
  const {
    staticData,
    data
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      background: {
        variant: "neutral_12"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const section = t1;
  const [period, setPeriod] = useState("120m");
  const [chartType, setChartType] = useState("interest");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      interest: 300000,
      rate: 300000,
      budget: 1500
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [inputValues, setInputValues] = useState(t2);
  let t3;
  if ($[2] !== chartType || $[3] !== inputValues || $[4] !== period || $[5] !== staticData) {
    const baseData = filterDataByPeriod(staticData[["120m", "180m"].includes(period) ? "180m" : "60m"], period);
    let t4;
    if ($[7] !== chartType || $[8] !== inputValues) {
      t4 = item => ({
        ...item,
        tenYearInterestRate: item.tenYearInterestRate ? withFormula(item.tenYearInterestRate, inputValues[chartType], chartType) : undefined,
        fifteenYearInterestRate: item.fifteenYearInterestRate ? withFormula(item.fifteenYearInterestRate, inputValues[chartType], chartType) : undefined,
        twentyYearInterestRate: item.twentyYearInterestRate ? withFormula(item.twentyYearInterestRate, inputValues[chartType], chartType) : undefined
      });
      $[7] = chartType;
      $[8] = inputValues;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = baseData.map(t4);
    $[2] = chartType;
    $[3] = inputValues;
    $[4] = period;
    $[5] = staticData;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const chartData = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value => setChartType(value as InterestRateChartType);
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== data.withMultiTabs) {
    t5 = data.withMultiTabs && <div className="w-full flex justify-between items-center mb-s"><TabsList className="flex m:flex-row flex-col gap-2xs">{chartOptions.map(_temp)}</TabsList></div>;
    $[11] = data.withMultiTabs;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = data.withMultiTabs && "py-m";
  let t7;
  if ($[13] !== t6) {
    t7 = clsx(t6);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== chartData || $[16] !== chartType || $[17] !== inputValues || $[18] !== period) {
    t8 = chartOptions.map(option_0 => <TabsContent key={option_0.id} className="flex flex-col gap-s" value={option_0.id}><p className="pb-m">{option_0.id === "rate" ? option_0.description(inputValues[chartType] * 0.9) : option_0.description(inputValues[chartType])}</p><div className="flex justify-between m:items-center flex-col m:flex-row">{option_0.inputLabel && <NumberField className="!w-auto" value={inputValues[chartType]} onChange={value_0 => {
          setInputValues({
            ...inputValues,
            [chartType]: value_0
          });
        }} size="sm" suffix={"\u20AC"} label={option_0.inputLabel} />}<div className="flex-1 flex m:justify-end"><PeriodSelect value={period} onChange={setPeriod} /></div></div><InterestRateChart disableAutoHeight={true} data={chartData} chartType={chartType} period={period} /></TabsContent>);
    $[15] = chartData;
    $[16] = chartType;
    $[17] = inputValues;
    $[18] = period;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = <div className={t7}>{t8}</div>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== chartType || $[24] !== t5 || $[25] !== t9) {
    t10 = <Tabs value={chartType} onValueChange={t4}>{t5}{t9}</Tabs>;
    $[23] = chartType;
    $[24] = t5;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = staticData["180m"];
  let t12;
  if ($[27] !== t11) {
    t12 = <RateTable observations={t11} />;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12) {
    t13 = <Section section={section} padding={false} className="px-s s:px-m py-m mt-m mb-2m flex flex-col gap-y">{t10}{t12}</Section>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};
export default InterestRateChartComponent;
function _temp(option) {
  return <TabsTrigger key={option.id} value={option.id}>{option.label}</TabsTrigger>;
}